import { create } from "zustand"

export type TerminalRuntimeStatus = "creating" | "running" | "exited"

export interface TerminalTab {
  id: string
  status: TerminalRuntimeStatus
  exitCode: number | null
}

interface TerminalStoreState {
  terminals: TerminalTab[]
  activeTerminalId: string | null
  syncFromServer: (terminalIds: string[]) => void
  markCreating: (id: string) => void
  markRunning: (id: string) => void
  markExited: (id: string, exitCode?: number | null) => void
  removeTerminal: (id: string) => void
  setActiveTerminal: (id: string) => void
  activateRelative: (direction: 1 | -1) => void
  reset: () => void
}

function dedupeIds(ids: string[]): string[] {
  const normalized = ids
    .map((id) => id.trim())
    .filter((id) => id.length > 0)
  return Array.from(new Set(normalized))
}

export const useTerminalStore = create<TerminalStoreState>()((set, get) => ({
  terminals: [],
  activeTerminalId: null,

  syncFromServer: (terminalIds) =>
    set((state) => {
      const ids = dedupeIds(terminalIds)
      if (ids.length === 0) {
        return {
          terminals: [],
          activeTerminalId: null,
        }
      }

      const existingById = new Map(state.terminals.map((tab) => [tab.id, tab]))
      const nextTabs: TerminalTab[] = ids.map((id) => {
        const existing = existingById.get(id)
        if (!existing) {
          return { id, status: "running", exitCode: null }
        }
        return {
          ...existing,
          status: "running",
          exitCode: null,
        }
      })

      const nextActive = nextTabs.some((tab) => tab.id === state.activeTerminalId)
        ? state.activeTerminalId
        : nextTabs[0]?.id ?? null

      return {
        terminals: nextTabs,
        activeTerminalId: nextActive,
      }
    }),

  markCreating: (id) =>
    set((state) => {
      if (state.terminals.some((tab) => tab.id === id)) {
        return {
          terminals: state.terminals.map((tab) =>
            tab.id === id
              ? { ...tab, status: "creating", exitCode: null }
              : tab,
          ),
          activeTerminalId: id,
        }
      }

      return {
        terminals: [...state.terminals, { id, status: "creating", exitCode: null }],
        activeTerminalId: id,
      }
    }),

  markRunning: (id) =>
    set((state) => {
      if (state.terminals.some((tab) => tab.id === id)) {
        return {
          terminals: state.terminals.map((tab) =>
            tab.id === id
              ? { ...tab, status: "running", exitCode: null }
              : tab,
          ),
          activeTerminalId: state.activeTerminalId ?? id,
        }
      }

      return {
        terminals: [...state.terminals, { id, status: "running", exitCode: null }],
        activeTerminalId: state.activeTerminalId ?? id,
      }
    }),

  markExited: (id, exitCode) =>
    set((state) => {
      if (!state.terminals.some((tab) => tab.id === id)) {
        return state
      }

      return {
        terminals: state.terminals.map((tab) =>
          tab.id === id
            ? { ...tab, status: "exited", exitCode: exitCode ?? null }
            : tab,
        ),
      }
    }),

  removeTerminal: (id) =>
    set((state) => {
      const removedIndex = state.terminals.findIndex((tab) => tab.id === id)
      if (removedIndex < 0) return state

      const nextTabs = state.terminals.filter((tab) => tab.id !== id)

      let nextActive = state.activeTerminalId
      if (state.activeTerminalId === id) {
        if (nextTabs.length === 0) {
          nextActive = null
        } else {
          const preferredIndex = Math.min(removedIndex, nextTabs.length - 1)
          nextActive = nextTabs[preferredIndex]?.id ?? null
        }
      } else if (!nextTabs.some((tab) => tab.id === state.activeTerminalId)) {
        nextActive = nextTabs[0]?.id ?? null
      }

      return {
        terminals: nextTabs,
        activeTerminalId: nextActive,
      }
    }),

  setActiveTerminal: (id) =>
    set((state) =>
      state.terminals.some((tab) => tab.id === id)
        ? { activeTerminalId: id }
        : state,
    ),

  activateRelative: (direction) => {
    const state = get()
    if (state.terminals.length <= 1) return

    const currentIndex = state.terminals.findIndex((tab) => tab.id === state.activeTerminalId)
    const baseIndex = currentIndex >= 0 ? currentIndex : 0
    const nextIndex = (baseIndex + direction + state.terminals.length) % state.terminals.length
    const nextId = state.terminals[nextIndex]?.id
    if (!nextId) return

    set({ activeTerminalId: nextId })
  },

  reset: () =>
    set({
      terminals: [],
      activeTerminalId: null,
    }),
}))
